/**
 * Visual guide: matches `documents/Web Version/website-standalone.html` —
 * outer cards tilted (-2° / +2°), center card straight, larger (scale-105, taller).
 * Side cards straighten on hover (md+). Middle stays prominent with z-10.
 */
const brand = { blue: '#2D68FE' };

const cardShell =
  'flex flex-col overflow-hidden rounded-[32px] bg-white shadow-2xl transition-transform duration-300 ease-out will-change-transform';

export function LandingWorkflowCards() {
  return (
    <div className="mt-14 sm:mt-20 w-full max-w-6xl mx-auto px-2 sm:px-4 overflow-visible">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-8 items-center relative">
        {/* Step 1 — tilted left; straight on hover (md+) */}
        <div
          className={`relative z-0 ${cardShell} border border-gray-100 min-h-[440px] h-[min(500px,70vh)] md:h-[500px] md:-rotate-2 md:hover:rotate-0 hover:z-[5]`}
        >
          <div className="flex items-center justify-between border-b border-slate-200/80 bg-slate-50 px-4 py-3">
            <div className="flex gap-1.5 pl-0.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Step 1: Create</span>
          </div>
          <div className="flex flex-1 flex-col gap-4 p-6">
            <div>
              <label className="mb-1 block text-[9px] font-black uppercase tracking-wide text-blue-600">Item name</label>
              <div className="h-10 rounded-xl border border-slate-100 bg-slate-50 px-3 flex items-center text-xs font-semibold text-slate-700">
                Solar Panels X100
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[9px] font-black uppercase tracking-wide text-blue-600">
                Ceiling price
              </label>
              <div className="h-10 rounded-xl border border-slate-100 bg-slate-50 px-3 flex items-center text-xs text-gray-500">
                ₹50,00,000
              </div>
            </div>
            <button
              type="button"
              tabIndex={-1}
              className="mt-1 h-12 w-full rounded-2xl text-sm font-black text-white shadow-lg"
              style={{ backgroundColor: brand.blue }}
            >
              Publish Auction
            </button>
            <div className="mt-auto rounded-xl border border-sky-100 bg-sky-50/80 px-3 py-3 text-xs font-medium leading-relaxed text-slate-600">
              Set your requirements, ceiling price, and invite vendors to compete.
            </div>
          </div>
        </div>

        {/* Step 2 — straight, scaled up, taller (standalone: z-10 scale-105 h-[550px]) */}
        <div
          className={`relative z-10 ${cardShell} border border-blue-100 min-h-[480px] h-[min(550px,75vh)] md:h-[550px] md:scale-105 shadow-blue-100/40`}
        >
          <div
            className="flex items-center justify-between border-b px-4 py-4 text-white"
            style={{ backgroundColor: brand.blue }}
          >
            <span className="text-[10px] font-black uppercase tracking-widest">Step 2: Live auction</span>
            <svg className="h-4 w-4 shrink-0 opacity-95" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
            </svg>
          </div>
          <div className="flex flex-1 flex-col gap-5 bg-slate-50/30 p-6">
            <div className="rounded-3xl border border-blue-50 bg-white p-4 text-center shadow-sm">
              <p className="text-[10px] font-black uppercase text-blue-600 mb-2">Time remaining</p>
              <p className="text-3xl font-black text-gray-900 tabular-nums tracking-tight">00:14:52</p>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 rounded-2xl bg-[#ecfdf5] px-3 py-2.5 ring-1 ring-emerald-100">
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-black text-white"
                  style={{ backgroundColor: '#22c55e' }}
                >
                  1
                </span>
                <span className="min-w-0 flex-1 text-xs font-bold text-slate-800">Vendor Alpha</span>
                <span className="text-xs font-black tabular-nums" style={{ color: brand.blue }}>
                  ₹42,65,000
                </span>
              </div>
              <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-3 py-2.5 shadow-sm">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-300 text-[11px] font-black text-white">
                  2
                </span>
                <span className="min-w-0 flex-1 text-xs font-bold text-slate-600">Vendor Beta</span>
                <span className="text-xs font-semibold tabular-nums text-slate-400">₹43,50,000</span>
              </div>
            </div>
            <div className="mt-auto rounded-2xl border-2 border-dashed border-blue-200 bg-white p-4 text-center">
              <p className="text-[10px] font-black uppercase text-gray-400 mb-1">Current savings</p>
              <p className="text-xl font-black text-green-500">₹7,35,000 Saved</p>
            </div>
          </div>
        </div>

        {/* Step 3 — tilted right; straight on hover (md+) */}
        <div
          className={`relative z-0 ${cardShell} border border-gray-100 min-h-[440px] h-[min(500px,70vh)] md:h-[500px] md:rotate-2 md:hover:rotate-0 hover:z-[5]`}
        >
          <div className="flex items-center justify-between border-b border-slate-200/80 bg-slate-50 px-4 py-3">
            <span className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
              <span aria-hidden>🏆</span>
              Step 3: Award
            </span>
            <span
              className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white"
              style={{ backgroundColor: '#22c55e' }}
              aria-hidden
            >
              ✓
            </span>
          </div>
          <div className="flex flex-1 flex-col p-6 text-center">
            <div
              className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full text-2xl text-green-600"
              style={{ backgroundColor: '#dcfce7' }}
              aria-hidden
            >
              🏅
            </div>
            <h3 className="text-lg font-black text-gray-900">Auction Ended</h3>
            <p className="mt-1 text-sm font-medium text-gray-500">Best price secured successfully.</p>
            <div className="mt-4 w-full rounded-2xl bg-slate-100 px-4 py-3 text-left">
              <p className="text-[10px] font-black uppercase tracking-wide text-gray-400">Winning vendor</p>
              <p className="mt-1 text-sm font-black text-gray-900">Vendor Alpha</p>
            </div>
            <div className="mt-6 w-full rounded-2xl px-4 py-4 text-white" style={{ backgroundColor: brand.blue }}>
              <p className="text-[10px] font-black uppercase opacity-60 mb-1">Final contract value</p>
              <p className="text-lg font-black tabular-nums">₹42,65,000</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
